import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import axios from "axios";
import {
    Search,
    Plus,
    Pencil,
    Trash2,
    Landmark,
    Download,
    X,
    FileText,
    AlertCircle,
    UserCheck,
    Coins,
} from "lucide-react";
import { API_BASE_URL, extractErrorMessage } from "../../../../api";
import { getAllUsersRequest } from "../../../redux/actions/userActions";
import {
    payrollFetchResourceRequest,
    payrollDeleteResourceRequest,
} from "../../../redux/actions/payrollActions";
import "./ParamRemunerations.css";

const ParamRemunerations = () => {
    const dispatch = useDispatch();

    // Redux states
    const users = useSelector((state) => state.user?.users || []);
    const remunerations = useSelector((state) => state.payroll?.remunerations || []);
    const loading = useSelector((state) => state.payroll?.loading || false);

    // Local states
    const [searchTerm, setSearchTerm] = useState("");
    const [openModal, setOpenModal] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    // Form states
    const [userId, setUserId] = useState("");
    const [banque, setBanque] = useState("");
    const [agence, setAgence] = useState("");
    const [rib, setRib] = useState("");
    const [taux, setTaux] = useState("");
    const [file, setFile] = useState(null);

    const token = sessionStorage.getItem("token");
    const uid = sessionStorage.getItem("uid");

    const headers = useMemo(() => ({
        Authorization: token?.startsWith("Bearer ") ? token : `Bearer ${token}`,
        uid,
    }), [token, uid]);

    // Initial load
    useEffect(() => {
        dispatch(getAllUsersRequest());
        dispatch(payrollFetchResourceRequest("remunerations"));
    }, [dispatch]);

    // Reset form
    const resetForm = () => {
        setUserId("");
        setBanque("");
        setAgence("");
        setRib("");
        setTaux("");
        setFile(null);
        setEditingItem(null);
    };

    // Open modal for add
    const handleAddClick = () => {
        resetForm();
        setOpenModal(true);
    };

    // Open modal for edit
    const handleEditClick = (item) => {
        setUserId(String(item.userId));
        setBanque(item.banque || "");
        setAgence(item.agence || "");
        setRib(item.rib || "");
        setTaux(item.taux || "");
        setFile(null);
        setEditingItem(item);
        setOpenModal(true);
    };

    // Delete item
    const handleDeleteClick = (id) => {
        Swal.fire({
            title: "Êtes-vous sûr ?",
            text: "Cette action est irréversible !",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Oui, supprimer !",
            cancelButtonText: "Annuler",
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(payrollDeleteResourceRequest("remunerations", id));
            }
        });
    };

    // Get user details
    const getUserName = (id) => {
        const u = users.find((usr) => String(usr.id) === String(id));
        if (!u) return `Collaborateur #${id}`;
        return u.fullName || `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.username;
    };

    const getUserCode = (id) => {
        const u = users.find((usr) => String(usr.id) === String(id));
        return u?.employeeCode || "-";
    };

    // Validate duplicate entries
    const isDuplicate = () => {
        const trimmedRib = rib.trim().toLowerCase();
        const trimmedBanque = banque.trim().toLowerCase();
        const trimmedAgence = agence.trim().toLowerCase();

        return remunerations.some((item) => {
            // If editing, skip comparing with itself
            if (editingItem && item.id === editingItem.id) {
                return false;
            }
            const itemRib = (item.rib || "").trim().toLowerCase();
            const itemBanque = (item.banque || "").trim().toLowerCase();
            const itemAgence = (item.agence || "").trim().toLowerCase();

            return (
                String(item.userId) === String(userId) &&
                itemRib === trimmedRib &&
                itemBanque === trimmedBanque &&
                itemAgence === trimmedAgence
            );
        });
    };

    // Submit form
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!userId) {
            Swal.fire({ icon: "warning", text: "Veuillez sélectionner un collaborateur." });
            return;
        }

        // Duplicate check on frontend
        if (isDuplicate()) {
            Swal.fire({
                icon: "warning",
                title: "Ligne existante",
                text: "Cette ligne (même collaborateur, banque, agence et RIB) existe déjà.",
            });
            return;
        }

        setSubmitting(true);

        const formData = new FormData();
        formData.append("userId", userId);
        formData.append("banque", banque.trim());
        formData.append("agence", agence.trim());
        formData.append("rib", rib.trim());
        if (taux !== "" && taux !== null) {
            formData.append("taux", taux);
        }
        if (file) {
            formData.append("attestationRib", file);
        }

        try {
            if (editingItem) {
                await axios.put(`${API_BASE_URL}/api/remunerations/${editingItem.id}`, formData, {
                    headers: {
                        ...headers,
                        "Content-Type": "multipart/form-data",
                    },
                });
                Swal.fire({
                    icon: "success",
                    title: "Modification enregistrée",
                    timer: 1400,
                    showConfirmButton: false,
                });
            } else {
                await axios.post(`${API_BASE_URL}/api/remunerations`, formData, {
                    headers: {
                        ...headers,
                        "Content-Type": "multipart/form-data",
                    },
                });
                Swal.fire({
                    icon: "success",
                    title: "Création enregistrée",
                    timer: 1400,
                    showConfirmButton: false,
                });
            }
            setOpenModal(false);
            resetForm();
            dispatch(payrollFetchResourceRequest("remunerations"));
        } catch (error) {
            const message = extractErrorMessage(error, "Erreur lors de l'enregistrement.");
            Swal.fire({ icon: "error", title: "Erreur", text: message });
        } finally {
            setSubmitting(false);
        }
    };

    // Filtered items
    const filteredItems = useMemo(() => {
        const q = searchTerm.trim().toLowerCase();
        return remunerations.filter((item) => {
            const userNameVal = getUserName(item.userId).toLowerCase();
            const userCodeVal = getUserCode(item.userId).toLowerCase();
            const bankVal = (item.banque || "").toLowerCase();
            const agencyVal = (item.agence || "").toLowerCase();
            const ribVal = (item.rib || "").toLowerCase();

            return (
                userNameVal.includes(q) ||
                userCodeVal.includes(q) ||
                bankVal.includes(q) ||
                agencyVal.includes(q) ||
                ribVal.includes(q)
            );
        });
    }, [remunerations, searchTerm, users]);

    // Download attestation
    const handleDownload = (filename) => {
        if (!filename) return;
        const url = `${API_BASE_URL}/api/remunerations/documents/attestation-rib/${filename}`;
        window.open(url, "_blank");
    };

    return (
        <div className="remun-container">
            <div className="remun-subheader">
                <div className="remun-subtitle-section">
                    <h3>Coordonnées Bancaires & RIBs</h3>
                    <p>Attribuez des coordonnées de virement (banque, agence, RIB) aux collaborateurs pour automatiser la génération des virements.</p>
                </div>
                <button className="remun-add-btn" onClick={handleAddClick}>
                    <Plus size={16} />
                    <span>Ajouter un RIB</span>
                </button>
            </div>

            <div className="remun-filter-bar">
                <div className="remun-search-wrapper">
                    <Search size={18} className="remun-search-icon" />
                    <input
                        type="text"
                        placeholder="Rechercher par nom, matricule, banque, RIB..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && (
                        <button className="remun-clear-search" onClick={() => setSearchTerm("")}>
                            <X size={16} />
                        </button>
                    )}
                </div>
            </div>

            {loading && remunerations.length === 0 ? (
                <div className="remun-loading-state">
                    <div className="remun-spinner"></div>
                    <p>Chargement des coordonnées bancaires...</p>
                </div>
            ) : filteredItems.length === 0 ? (
                <div className="remun-empty-state">
                    <AlertCircle size={40} />
                    <p>{searchTerm ? "Aucune ligne ne correspond à votre recherche." : "Aucune coordonnée bancaire enregistrée pour le moment."}</p>
                </div>
            ) : (
                <div className="remun-table-wrapper">
                    <table className="remun-table">
                        <thead>
                            <tr>
                                <th>Collaborateur</th>
                                <th>Matricule</th>
                                <th>Banque</th>
                                <th>Agence</th>
                                <th>RIB</th>
                                <th>Taux (%)</th>
                                <th>Attestation</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredItems.map((item) => (
                                <tr key={item.id}>
                                    <td className="remun-collab-cell">
                                        <div className="remun-avatar">
                                            {getUserName(item.userId).charAt(0).toUpperCase()}
                                        </div>
                                        <div className="remun-collab-info">
                                            <span className="remun-collab-name">{getUserName(item.userId)}</span>
                                        </div>
                                    </td>
                                    <td>
                                        <span className="remun-code-badge">{getUserCode(item.userId)}</span>
                                    </td>
                                    <td>
                                        <strong>{item.banque || "-"}</strong>
                                    </td>
                                    <td>{item.agence || "-"}</td>
                                    <td className="remun-rib-cell">{item.rib || "-"}</td>
                                    <td>{item.taux !== null && item.taux !== undefined ? `${item.taux} %` : "-"}</td>
                                    <td>
                                        {item.attestationRib ? (
                                            <button
                                                className="remun-download-btn"
                                                title="Télécharger l'attestation RIB (PDF)"
                                                onClick={() => handleDownload(item.attestationRib)}
                                            >
                                                <Download size={14} />
                                                <span>Télécharger</span>
                                            </button>
                                        ) : (
                                            <span className="remun-no-file">Aucune</span>
                                        )}
                                    </td>
                                    <td className="remun-actions-cell">
                                        <button
                                            className="remun-action-btn edit"
                                            title="Modifier"
                                            onClick={() => handleEditClick(item)}
                                        >
                                            <Pencil size={14} />
                                        </button>
                                        <button
                                            className="remun-action-btn delete"
                                            title="Supprimer"
                                            onClick={() => handleDeleteClick(item.id)}
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Form Modal */}
            {openModal && (
                <div className="remun-modal-overlay">
                    <div className="remun-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="remun-modal-header">
                            <h3>{editingItem ? "Modifier" : "Ajouter"} des coordonnées bancaires</h3>
                            <button className="remun-modal-close" onClick={() => setOpenModal(false)}>
                                <X size={20} />
                            </button>
                        </div>
                        <form onSubmit={handleSubmit} className="remun-form">
                            <div className="remun-section-title">Coordonnées bancaires</div>
                            <div className="remun-form-group">
                                <label>Collaborateur *</label>
                                <select
                                    value={userId}
                                    onChange={(e) => setUserId(e.target.value)}
                                    disabled={!!editingItem}
                                    required
                                >
                                    <option value="">-- Sélectionner un collaborateur --</option>
                                    {users.map((u) => (
                                        <option key={u.id} value={u.id}>
                                            [{u.employeeCode || "N/A"}] {u.fullName || `${u.firstName || ""} ${u.lastName || ""}`.trim()}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="remun-form-row">
                                <div className="remun-form-group">
                                    <label>Banque *</label>
                                    <input
                                        type="text"
                                        placeholder="Ex: Attijariwafa Bank, BCP..."
                                        value={banque}
                                        onChange={(e) => setBanque(e.target.value)}
                                        maxLength={150}
                                        required
                                    />
                                </div>
                                <div className="remun-form-group">
                                    <label>Agence *</label>
                                    <input
                                        type="text"
                                        placeholder="Ex: Agence Centre, Boulevard..."
                                        value={agence}
                                        onChange={(e) => setAgence(e.target.value)}
                                        maxLength={150}
                                        required
                                    />
                                </div>
                            </div>

                            <div className="remun-form-row">
                                <div className="remun-form-group">
                                    <label>RIB (Relevé d'Identité Bancaire) *</label>
                                    <input
                                        type="text"
                                        placeholder="24 chiffres"
                                        value={rib}
                                        onChange={(e) => setRib(e.target.value)}
                                        maxLength={100}
                                        required
                                    />
                                </div>
                                <div className="remun-form-group">
                                    <label>Taux de rémunération (%)</label>
                                    <input
                                        type="number"
                                        placeholder="Ex: 100"
                                        value={taux}
                                        onChange={(e) => setTaux(e.target.value)}
                                        step="0.0001"
                                        min="0"
                                        max="100"
                                    />
                                </div>
                            </div>

                            <div className="remun-form-group">
                                <label>Attestation RIB (Fichier PDF uniquement, max 10 Mo)</label>
                                <div className="remun-file-upload">
                                    <input
                                        type="file"
                                        accept=".pdf"
                                        onChange={(e) => setFile(e.target.files[0])}
                                    />
                                    {editingItem?.attestationRib && !file && (
                                        <p className="remun-file-hint">
                                            Fichier actuel : {editingItem.attestationRib.substring(37)}
                                        </p>
                                    )}
                                </div>
                            </div>

                            <div className="remun-modal-footer">
                                <button
                                    type="button"
                                    className="remun-cancel-btn"
                                    onClick={() => setOpenModal(false)}
                                    disabled={submitting}
                                >
                                    Annuler
                                </button>
                                <button
                                    type="submit"
                                    className="remun-submit-btn"
                                    disabled={submitting}
                                >
                                    {submitting ? "Enregistrement..." : editingItem ? "Modifier" : "Enregistrer"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ParamRemunerations;
